/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, NgZone} from '@angular/core';
import * as _ from 'lodash';
import template from './team-group-edit-page.component.html';
import {AppEventBusService} from '../../common/events/app-event-bus.service';

import {LocalNetworkInfo} from '../../../components/session/local-network-info';
import {AdminResourceEditPageBase} from '../../common/controller/admin-resource-edit-page-base';
import {TeamResource} from '../../../components/api/resources/resource/team.resource';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {deferMarkForCheck} from '../../common/util/defer-mark-for-check';

@Component({
  standalone: false,
  selector: 'adn-team-group-edit-page',
  template
})
export class TeamGroupEditPageComponent extends AdminResourceEditPageBase {
  teamTypes: string[] = [];
  allTeams: any[] | null = null;
  canUpdateTeamGroup = false;
  teamsWarning = false;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private LocalUserPermissions: any,
    @Inject(TeamResource) private Team: any,
    private cdr: ChangeDetectorRef,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.canUpdateTeamGroup = this.LocalUserPermissions.hasNetwork('MANAGE_SYSTEM');
    this.teamTypes = getAvailableTeamTypes(this.LocalNetworkProfile);

    this.Team.query({minimal: true}).$promise.then((page: any) => {
      // null → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.allTeams = page.results;
      });
    });
  }

  get filteredTeams(): any[] {
    if (!this.editingModel?.type || !this.allTeams) {
      return [];
    }
    return _.filter(this.allTeams, ['type', this.editingModel.type]);
  }

  protected afterInitEditingModel(): void {
    this.editingModel.teams = this.normalizeTeamIds(this.editingModel.teams);
  }

  protected afterInitAfterSave(saveObj?: any): void {
    if (!saveObj) {
      return;
    }
    this.editingModel.teams = this.normalizeTeamIds(saveObj.teams);
    this.editingModel.type = _.get(saveObj, 'type') || this.editingModel.type;
  }

  protected onBeforeSubmit(): void {
    if (this.isNew) {
      LocalNetworkInfo.obtain().increment('teamGroupsCount');
    }
  }

  deselectTeams(): void {
    this.editingModel.teams = [];
    this.markExternalFormDirty();
  }

  selectTeams(): void {
    this.editingModel.teams = _.map(this.filteredTeams, 'id');
    this.markExternalFormDirty();
  }

  onTypeChange(): void {
    this.deselectTeams();
  }

  isTeamSelected(teamId: string): boolean {
    return (this.editingModel.teams || []).indexOf(teamId) > -1;
  }

  toggleTeam(teamId: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const teams = [...(this.editingModel.teams || [])];
    const index = teams.indexOf(teamId);

    if (checked && index < 0) {
      teams.push(teamId);
    } else if (!checked && index > -1) {
      teams.splice(index, 1);
    }

    this.editingModel.teams = teams;
    this.markExternalFormDirty();
  }

  trackByTeamId(_index: number, team: any): string {
    return team.id;
  }

  private normalizeTeamIds(teams: any): string[] {
    return _.map(teams || [], (team: any) => _.isString(team) ? team : team.id);
  }
}

function getAvailableTeamTypes(LocalNetworkProfile: any): string[] {
  const isSuiPlatform = LocalNetworkProfile.isSuiPlatform();
  const isAdvertisingPlatform = LocalNetworkProfile.isAdvertisingPlatform();
  const isMarketplacePlatform = LocalNetworkProfile.isMarketplacePlatform();

  let teamTypes: string[] = [];
  if (isSuiPlatform) {
    teamTypes = ['STANDARD', 'SUI', 'AX_ADVERTISER', 'AX_PUBLISHER'];
  } else if (isAdvertisingPlatform) {
    teamTypes = ['STANDARD'];
    if (isMarketplacePlatform) {
      teamTypes.push('AX_ADVERTISER');
      teamTypes.push('AX_PUBLISHER');
      teamTypes.push('CREATIVE_AGENCY');
    }
  }

  if (LocalNetworkProfile.get('politicalApp')) {
    teamTypes = _.uniq(teamTypes.concat(['CONTROLLER', 'SPONSOR']));
  }

  return teamTypes;
}
